// Tipos de erro
export const ERROR_TYPES = {
    VALIDATION: 'VALIDATION',
    NETWORK: 'NETWORK',
    AUTH: 'AUTH',
    BUSINESS: 'BUSINESS',
    SERVER: 'SERVER',
    UNKNOWN: 'UNKNOWN'
};

// Mensagens de erro amigáveis
export const ERROR_MESSAGES = {
    // Erros de autenticação
    'AUTH_001': 'Sua sessão expirou. Por favor, faça login novamente.',
    'AUTH_002': 'Você não tem permissão para realizar esta ação.',
    
    // Erros de validação
    'VAL_001': 'Por favor, preencha todos os campos obrigatórios.',
    'VAL_002': 'O formato do campo é inválido.',
    
    // Erros de rede
    'NET_001': 'Não foi possível conectar ao servidor. Verifique sua conexão.',
    'NET_002': 'A operação demorou muito para responder. Tente novamente.',
    
    // Erros de negócio
    'BUS_001': 'Este registro já existe no sistema.',
    'BUS_002': 'O registro não pode ser excluído pois está em uso.',
    
    // Erros de servidor
    'SRV_001': 'Ocorreu um erro interno no servidor.',
    'SRV_002': 'O serviço está temporariamente indisponível.'
};

class ErrorService {
    constructor() {
        this.retryAttempts = 3;
        this.retryDelay = 1000; // 1 segundo
    }

    /**
     * Determina o tipo de erro com base no erro recebido
     */
    getErrorType(error) {
        if (!error) return ERROR_TYPES.UNKNOWN;

        if (error.status === 401 || error.status === 403) {
            return ERROR_TYPES.AUTH;
        }

        if (error.status === 400) {
            return ERROR_TYPES.VALIDATION;
        }

        if (error.status === 404 || error.status === 409) {
            return ERROR_TYPES.BUSINESS;
        }

        if (error.status >= 500) {
            return ERROR_TYPES.SERVER;
        }

        if (!navigator.onLine || error.message?.includes('network')) {
            return ERROR_TYPES.NETWORK;
        }

        return ERROR_TYPES.UNKNOWN;
    }

    /**
     * Obtém uma mensagem amigável para o erro
     */
    getFriendlyMessage(error) {
        if (!error) return 'Ocorreu um erro inesperado.';

        // Se tiver um código de erro específico
        if (error.code && ERROR_MESSAGES[error.code]) {
            return ERROR_MESSAGES[error.code];
        }

        // Mensagens baseadas no tipo de erro
        const errorType = this.getErrorType(error);
        switch (errorType) {
            case ERROR_TYPES.AUTH:
                return 'Erro de autenticação. Por favor, faça login novamente.';
            case ERROR_TYPES.VALIDATION:
                return error.message || 'Os dados informados são inválidos.';
            case ERROR_TYPES.NETWORK:
                return 'Erro de conexão. Verifique sua internet e tente novamente.';
            case ERROR_TYPES.BUSINESS:
                return error.message || 'Não foi possível completar a operação.';
            case ERROR_TYPES.SERVER:
                return 'Erro no servidor. Tente novamente em alguns instantes.';
            default:
                return error.message || 'Ocorreu um erro inesperado.';
        }
    }

    /**
     * Trata o erro e retorna um objeto com informações formatadas
     */
    handleError(error) {
        const errorType = this.getErrorType(error);
        const friendlyMessage = this.getFriendlyMessage(error);

        // Log do erro para análise posterior
        this.logError(error);

        return {
            type: errorType,
            message: friendlyMessage,
            originalError: error,
            timestamp: new Date().toISOString()
        };
    }

    /**
     * Tenta executar uma operação com retry automático para erros de rede
     */
    async retryOperation(operation, maxAttempts = this.retryAttempts) {
        let lastError;

        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
            try {
                return await operation();
            } catch (error) {
                lastError = error;
                
                if (this.getErrorType(error) !== ERROR_TYPES.NETWORK || attempt === maxAttempts) {
                    throw this.handleError(error);
                }

                await this.delay(this.retryDelay * attempt);
            }
        }

        throw this.handleError(lastError);
    }

    /**
     * Cria um delay promise para uso no retry
     */
    delay(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    /**
     * Registra o erro para análise posterior
     */
    logError(error) {
        const errorLog = {
            timestamp: new Date().toISOString(),
            type: this.getErrorType(error),
            message: error.message,
            stack: error.stack,
            url: window.location.href,
            userAgent: navigator.userAgent
        };

        // Aqui você pode implementar a lógica de logging
        // Por exemplo, enviar para um serviço de monitoramento
        console.error('Error Log:', errorLog);
    }
}

export const errorService = new ErrorService();
export default errorService; 